import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  createRubric,
  deleteRubric,
  listRubrics,
  updateRubric,
} from './rubricsAdminApi'
import {
  RubricCreateInput,
  RubricFilters,
  RubricUpdateInput,
} from './rubricsAdminModel'

const ADMIN_LIST_KEY = ['rubricsAdmin', 'list'] as const

export const useRubricsListQuery = (filters: RubricFilters) => {
  return useQuery({
    queryKey: [...ADMIN_LIST_KEY, filters],
    queryFn: () => listRubrics(filters),
    retry: false,
    // Hold the prior page on the screen while a new search/page loads.
    keepPreviousData: true,
    onError: () => {
      notification.error({ message: 'Failed to load rubrics' })
    },
  })
}

export const useCreateRubricMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: RubricCreateInput) => createRubric(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Rubric created' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to create rubric', description })
    },
  })
}

export const useUpdateRubricMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((input: RubricUpdateInput) => updateRubric(input), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Rubric updated' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to update rubric', description })
    },
  })
}

export const useDeleteRubricMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((rubricId: string) => deleteRubric(rubricId), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
      notification.success({ message: 'Rubric deleted' })
    },
    onError: (err) => {
      const description = err instanceof Error ? err.message : undefined
      notification.error({ message: 'Failed to delete rubric', description })
    },
  })
}
